"use strict";
/**
  * @reach/menu-button v0.18.0
  *
  * Copyright (c) 2018-2022, React Training LLC
  *
  * This source code is licensed under the MIT license found in the
  * LICENSE.md file in the root directory of this source tree.
  *
  * @license MIT
  */
// src/reach-menu-button.tsx

const _excluded = ["as", "id", "children"],
      _excluded2 = ["as"],
      _excluded3 = ["as"],
      _excluded4 = ["as"],
      _excluded5 = ["as"],
      _excluded6 = ["as", "component", "onSelect"],
      _excluded7 = ["portal"],
      _excluded8 = ["as"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import * as React from "react";
import { Popover } from "@reach/popover";
import { DropdownProvider, useDropdownItem, useDropdownItems, useDropdownPopover, useDropdownTrigger, useDropdownContext } from "@reach/dropdown";
import { noop } from "@reach/utils";
import { isFragment } from "react-is";
var Menu = /*#__PURE__*/React.forwardRef((_ref, forwardedRef) => {
  let {
    as: Comp = React.Fragment,
    id,
    children
  } = _ref,
      rest = _objectWithoutProperties(_ref, _excluded);

  let parentIsFragment = React.useMemo(() => {
    try {
      return isFragment( /* @__PURE__ */React.createElement(Comp, null));
    } catch (err) {
      return false;
    }
  }, [Comp]);
  let props = parentIsFragment ? {} : _objectSpread({
    ref: forwardedRef,
    id,
    "data-reach-menu": ""
  }, rest);
  return /* @__PURE__ */React.createElement(Comp, _objectSpread({}, props), /* @__PURE__ */React.createElement(DropdownProvider, {
    id,
    children
  }));
});
Menu.displayName = "Menu";
var MenuButton = /*#__PURE__*/React.forwardRef((_ref2, forwardedRef) => {
  let {
    as: Comp = "button"
  } = _ref2,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  let {
    data: {
      isExpanded,
      controls
    },
    props
  } = useDropdownTrigger(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-expanded": isExpanded ? true : void 0,
    "aria-haspopup": true,
    "aria-controls": controls
  }, props), {}, {
    "data-reach-menu-button": ""
  }));
});
MenuButton.displayName = "MenuButton";
var MenuItemImpl = /*#__PURE__*/React.forwardRef((_ref3, forwardedRef) => {
  let {
    as: Comp = "div"
  } = _ref3,
      rest = _objectWithoutProperties(_ref3, _excluded3);

  let {
    data: {
      disabled
    },
    props
  } = useDropdownItem(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    role: "menuitem"
  }, props), {}, {
    "aria-disabled": disabled || void 0,
    "data-reach-menu-item": ""
  }));
});
MenuItemImpl.displayName = "MenuItemImpl";
var MenuItem = /*#__PURE__*/React.forwardRef((_ref4, forwardedRef) => {
  let {
    as = "div"
  } = _ref4,
      props = _objectWithoutProperties(_ref4, _excluded4);

  return /* @__PURE__ */React.createElement(MenuItemImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    as
  }));
});
MenuItem.displayName = "MenuItem";
var MenuItems = /*#__PURE__*/React.forwardRef((_ref5, forwardedRef) => {
  let {
    as: Comp = "div"
  } = _ref5,
      rest = _objectWithoutProperties(_ref5, _excluded5);

  let {
    data: {
      activeDescendant,
      triggerId
    },
    props
  } = useDropdownItems(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  return /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({
    "aria-activedescendant": activeDescendant,
    "aria-labelledby": triggerId || void 0,
    role: "menu"
  }, props), {}, {
    "data-reach-menu-items": ""
  }));
});
MenuItems.displayName = "MenuItems";
var MenuLink = /*#__PURE__*/React.forwardRef((_ref6, forwardedRef) => {
  let {
    as = "a",
    component,
    onSelect
  } = _ref6,
      props = _objectWithoutProperties(_ref6, _excluded6);

  if (true) {
    React.useEffect(() => {
      if (component) {
        console.warn("[@reach/menu-button]: Please use the `as` prop instead of `component`");
      }
    }, [component]);
  }

  return /* @__PURE__ */React.createElement(MenuItemImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    "data-reach-menu-link": "",
    as,
    isLink: true,
    onSelect: onSelect || noop
  }));
});
MenuLink.displayName = "MenuLink";
var MenuList = /*#__PURE__*/React.forwardRef((_ref7, forwardedRef) => {
  let {
    portal = true
  } = _ref7,
      props = _objectWithoutProperties(_ref7, _excluded7);

  return /* @__PURE__ */React.createElement(MenuPopover, {
    portal
  }, /* @__PURE__ */React.createElement(MenuItems, _objectSpread(_objectSpread({}, props), {}, {
    ref: forwardedRef,
    "data-reach-menu-list": ""
  })));
});
MenuList.displayName = "MenuList";
var MenuPopover = /*#__PURE__*/React.forwardRef((_ref8, forwardedRef) => {
  let {
    as: Comp = "div"
  } = _ref8,
      rest = _objectWithoutProperties(_ref8, _excluded8);

  let {
    data: {
      portal,
      targetRef,
      position
    },
    props
  } = useDropdownPopover(_objectSpread(_objectSpread({}, rest), {}, {
    ref: forwardedRef
  }));
  let sharedProps = {
    "data-reach-menu-popover": ""
  };
  return portal ? /* @__PURE__ */React.createElement(Popover, _objectSpread(_objectSpread(_objectSpread({}, props), sharedProps), {}, {
    as: Comp,
    targetRef,
    position,
    unstable_skipInitialPortalRender: true
  })) : /* @__PURE__ */React.createElement(Comp, _objectSpread(_objectSpread({}, props), sharedProps));
});
MenuPopover.displayName = "MenuPopover";

function useMenuButtonContext() {
  let {
    state: {
      isExpanded
    }
  } = useDropdownContext("useMenuButtonContext");
  return React.useMemo(() => ({
    isExpanded
  }), [isExpanded]);
}

export { Menu, MenuButton, MenuItem, MenuItems, MenuLink, MenuList, MenuPopover, useMenuButtonContext };